import { GeckoColorScale } from 'color-scale-kit'
import {
  DataType,
  GeckoColorScaleThreshold,
  GeckoComponentDataFrame,
  GeckoComponentRect,
  GeckoMeasurement,
  GeckoMeasurementRange,
  GeckoMeasurementUnit,
  GeckoView,
} from 'types'

import type {
  ColorScaleInfo,
  CroppedValues,
  DisplayBounds,
  NormalizedBounds,
  ThresholdSignature,
} from './types'
import { clamp, lerp } from './utils'

export function cropValues(
  cdf: GeckoComponentDataFrame,
  bounds: GeckoComponentRect,
  rawValues: Readonly<Float32Array>,
  rawValuesXBinCount?: number
): CroppedValues {
  const normalizedBounds = normalizeBounds(cdf, bounds)
  const geometry = cdf.data.geometry
    .project(bounds.space)
    .project(cdf.data.geometry.space)
  const binWidth = cdf.data.binning.bin_size.width.toNumber(geometry.x.unit)
  const binHeight = cdf.data.binning.bin_size.height.toNumber(geometry.y.unit)
  const geometryXMin = geometry.x.min.toNumber(geometry.x.unit)
  const geometryYMin = geometry.y.min.toNumber(geometry.y.unit)
  const projectedBounds = normalizedBounds.rect
  const requestedXStartIndex = Math.floor(
    (projectedBounds.x.min.toNumber(geometry.x.unit) - geometryXMin) / binWidth
  )
  const requestedXEndIndex = Math.max(
    requestedXStartIndex + 1,
    Math.ceil(
      (projectedBounds.x.max.toNumber(geometry.x.unit) - geometryXMin) /
        binWidth
    )
  )
  const requestedYStartIndex = Math.floor(
    (projectedBounds.y.min.toNumber(geometry.y.unit) - geometryYMin) / binHeight
  )
  const requestedYEndIndex = Math.max(
    requestedYStartIndex + 1,
    Math.ceil(
      (projectedBounds.y.max.toNumber(geometry.y.unit) - geometryYMin) /
        binHeight
    )
  )
  const sourceXStartIndex = clamp(
    requestedXStartIndex,
    0,
    cdf.data.binning.x_bin_count
  )
  const sourceXEndIndex = clamp(
    requestedXEndIndex,
    0,
    cdf.data.binning.x_bin_count
  )
  const sourceYStartIndex = clamp(
    requestedYStartIndex,
    0,
    cdf.data.binning.y_bin_count
  )
  const sourceYEndIndex = clamp(
    requestedYEndIndex,
    0,
    cdf.data.binning.y_bin_count
  )

  const xBinCount = requestedXEndIndex - requestedXStartIndex
  const yBinCount = requestedYEndIndex - requestedYStartIndex
  const values = new Float32Array(xBinCount * yBinCount)
  values.fill(Number.NaN)

  const compactValues = cropCompactValues(
    rawValues,
    xBinCount,
    yBinCount,
    rawValuesXBinCount
  )
  if (compactValues) {
    return {
      values: compactValues,
      xBinCount,
      yBinCount,
      bounds: normalizedBounds,
    }
  }

  const sourceXBinCount = sourceXEndIndex - sourceXStartIndex
  const sourceYBinCount = sourceYEndIndex - sourceYStartIndex
  const targetXOffset = sourceXStartIndex - requestedXStartIndex
  const targetYOffset = sourceYStartIndex - requestedYStartIndex

  copyBinnedValues({
    sourceValues: rawValues,
    sourceXBinCount,
    sourceYBinCount,
    sourceRowStride: cdf.data.binning.x_bin_count,
    sourceXOffset: sourceXStartIndex,
    sourceYOffset: sourceYStartIndex,
    targetValues: values,
    targetXBinCount: xBinCount,
    targetCopyXBinCount: sourceXBinCount,
    targetCopyYBinCount: sourceYBinCount,
    targetXOffset,
    targetYOffset,
  })

  return {
    values,
    xBinCount,
    yBinCount,
    bounds: normalizedBounds,
  }
}

function cropCompactValues(
  rawValues: Readonly<Float32Array>,
  targetXBinCount: number,
  targetYBinCount: number,
  rawValuesXBinCount?: number
) {
  if (
    rawValues.length === 0 ||
    (!rawValuesXBinCount &&
      rawValues.length !== targetXBinCount * targetYBinCount)
  ) {
    return undefined
  }

  const sourceXBinCount = Math.min(
    rawValues.length,
    rawValuesXBinCount && rawValuesXBinCount > 0
      ? rawValuesXBinCount
      : targetXBinCount
  )
  if (sourceXBinCount <= 0) {
    return undefined
  }

  const sourceYBinCount = Math.floor(rawValues.length / sourceXBinCount)
  if (sourceYBinCount <= 0) {
    return undefined
  }

  const values = new Float32Array(targetXBinCount * targetYBinCount)
  copyBinnedValues({
    sourceValues: rawValues,
    sourceXBinCount,
    sourceYBinCount,
    sourceRowStride: sourceXBinCount,
    targetValues: values,
    targetXBinCount,
    targetCopyXBinCount: targetXBinCount,
    targetCopyYBinCount: targetYBinCount,
    scaleSourceToTarget: true,
  })

  return values
}

type BinnedValueCopyConfig = {
  sourceValues: Readonly<Float32Array>
  sourceXBinCount: number
  sourceYBinCount: number
  sourceRowStride: number
  sourceXOffset?: number
  sourceYOffset?: number
  targetValues: Float32Array
  targetXBinCount: number
  targetCopyXBinCount: number
  targetCopyYBinCount: number
  targetXOffset?: number
  targetYOffset?: number
  scaleSourceToTarget?: boolean
}

function copyBinnedValues({
  sourceValues,
  sourceXBinCount,
  sourceYBinCount,
  sourceRowStride,
  sourceXOffset = 0,
  sourceYOffset = 0,
  targetValues,
  targetXBinCount,
  targetCopyXBinCount,
  targetCopyYBinCount,
  targetXOffset = 0,
  targetYOffset = 0,
  scaleSourceToTarget = false,
}: BinnedValueCopyConfig) {
  for (let targetY = 0; targetY < targetCopyYBinCount; targetY++) {
    const sourceY =
      sourceYOffset +
      (scaleSourceToTarget
        ? Math.min(
            sourceYBinCount - 1,
            Math.floor((targetY * sourceYBinCount) / targetCopyYBinCount)
          )
        : targetY)
    const sourceRowOffset = sourceY * sourceRowStride
    const targetRowOffset = (targetYOffset + targetY) * targetXBinCount

    for (let targetX = 0; targetX < targetCopyXBinCount; targetX++) {
      const sourceX =
        sourceXOffset +
        (scaleSourceToTarget
          ? Math.min(
              sourceXBinCount - 1,
              Math.floor((targetX * sourceXBinCount) / targetCopyXBinCount)
            )
          : targetX)

      targetValues[targetRowOffset + targetXOffset + targetX] =
        sourceValues[sourceRowOffset + sourceX] ?? Number.NaN
    }
  }
}

export function normalizeBounds(
  cdf: GeckoComponentDataFrame,
  bounds: GeckoComponentRect
): NormalizedBounds {
  const projected = bounds.project(cdf.data.geometry.space)
  const projectedXRange =
    projected.width.num > Number.EPSILON ? projected.x : cdf.data.geometry.x
  const projectedYRange =
    projected.height.num > Number.EPSILON ? projected.y : cdf.data.geometry.y
  const xUnit = cdf.data.geometry.x.unit
  const yUnit = cdf.data.geometry.y.unit
  const geometryXMin = cdf.data.geometry.x.min.toNumber(xUnit)
  const geometryYMin = cdf.data.geometry.y.min.toNumber(yUnit)
  const xBinWidth = cdf.data.binning.bin_size.width.toNumber(xUnit)
  const yBinHeight = cdf.data.binning.bin_size.height.toNumber(yUnit)
  const requestedXStart = projectedXRange.min.toNumber(xUnit)
  const requestedXEnd = projectedXRange.max.toNumber(xUnit)
  const requestedYStart = projectedYRange.min.toNumber(yUnit)
  const requestedYEnd = projectedYRange.max.toNumber(yUnit)
  const xStartIndex = Math.floor((requestedXStart - geometryXMin) / xBinWidth)
  const xEndIndex = Math.max(
    xStartIndex + 1,
    Math.ceil((requestedXEnd - geometryXMin) / xBinWidth)
  )
  const yStartIndex = Math.floor((requestedYStart - geometryYMin) / yBinHeight)
  const yEndIndex = Math.max(
    yStartIndex + 1,
    Math.ceil((requestedYEnd - geometryYMin) / yBinHeight)
  )
  const xStart = geometryXMin + xStartIndex * xBinWidth
  const xEnd = geometryXMin + xEndIndex * xBinWidth
  const yStart = geometryYMin + yStartIndex * yBinHeight
  const yEnd = geometryYMin + yEndIndex * yBinHeight

  return {
    rect: GeckoComponentRect.CreateFromXYs(
      cdf.data.geometry.componentID,
      xStart,
      yStart,
      xEnd,
      yEnd,
      xUnit,
      yUnit
    ),
    xStart,
    xEnd,
    yStart,
    yEnd,
  }
}

export function normalizeThreshold(
  threshold: GeckoColorScaleThreshold | undefined,
  unit: GeckoMeasurementUnit
): ThresholdSignature | undefined {
  if (!threshold) return undefined

  const converted = threshold.convertTo(resolveValueDisplayUnit(unit))
  return {
    min: converted.min.num,
    mid: converted.mid.num,
    max: converted.max.num,
    unit: converted.min.unit,
  }
}

export function createColorScaleInfo(
  cdf: GeckoComponentDataFrame,
  threshold: GeckoColorScaleThreshold | undefined,
  values: Float32Array
): ColorScaleInfo {
  const measurementUnit = resolveValueDisplayUnit(
    cdf.data.params.measurement_unit
  )
  const valueRange = createValueRange(values, measurementUnit)
  const defaultThreshold = determineDefaultColorScaleThresholdNonCdf(
    cdf.data.params.view,
    measurementUnit,
    valueRange
  )
  const selectedThreshold = (threshold ?? defaultThreshold)?.convertTo(
    measurementUnit
  )
  const scaleMin = selectedThreshold?.min.num ?? valueRange?.min.num ?? 0
  const scaleMid =
    selectedThreshold?.mid.num ??
    valueRange?.min.plus(valueRange.max).scale(0.5).num ??
    0.5
  const scaleMax = selectedThreshold?.max.num ?? valueRange?.max.num ?? 1
  const rangeMin = valueRange?.min.num ?? scaleMin
  const rangeMax = valueRange?.max.num ?? scaleMax

  const valuesToColorRange = Array.from(
    new Set(
      [rangeMin, rangeMax, scaleMin, scaleMid, scaleMax].filter(
        (value) => value >= rangeMin && value <= rangeMax
      )
    )
  )
    .sort((a, b) => a - b)
    .map<[number, number]>((value) => {
      const { start, middle, end } = cdf.data.params.view.colorcet

      if (value <= scaleMin) return [value, start]
      if (value === scaleMid) return [value, middle]
      if (value >= scaleMax) return [value, end]

      return [
        value,
        value < scaleMid
          ? lerp(start, middle, (value - scaleMin) / (scaleMid - scaleMin))
          : lerp(middle, end, (value - scaleMid) / (scaleMax - scaleMid)),
      ]
    })

  return {
    colorScale: cdf.data.params.view.colorcet.customColorScale
      ? new GeckoColorScale({
          customColorScale: cdf.data.params.view.colorcet.customColorScale,
          valuesToColorRange,
        })
      : new GeckoColorScale({
          colorcetId: cdf.data.params.view.colorcet.colorcetId,
          valuesToColorRange,
        }),
    min: scaleMin,
    mid: scaleMid,
    max: scaleMax,
    measurementUnit,
  }
}

export function createDisplayBounds(bounds: NormalizedBounds): DisplayBounds {
  const xUnit = resolveAxisDisplayUnit(bounds.rect.x.unit)
  const yUnit = resolveAxisDisplayUnit(bounds.rect.y.unit)

  return {
    x: {
      start: bounds.rect.x.min.toNumber(xUnit),
      end: bounds.rect.x.max.toNumber(xUnit),
      unit: xUnit,
    },
    y: {
      start: bounds.rect.y.min.toNumber(yUnit),
      end: bounds.rect.y.max.toNumber(yUnit),
      unit: yUnit,
    },
  }
}

export function resolveAxisDisplayUnit(unit: GeckoMeasurementUnit) {
  return isImperialDistanceUnit(unit) ? GeckoMeasurementUnit.Feet : unit
}

export function resolveValueDisplayUnit(unit: GeckoMeasurementUnit) {
  return isImperialDistanceUnit(unit) ? GeckoMeasurementUnit.Inches : unit
}

export function isImperialDistanceUnit(unit: GeckoMeasurementUnit) {
  return (
    unit === GeckoMeasurementUnit.Meters ||
    unit === GeckoMeasurementUnit.Millimeters ||
    unit === GeckoMeasurementUnit.Feet ||
    unit === GeckoMeasurementUnit.Inches
  )
}

export function convertValuesToDisplayUnit(
  values: Readonly<Float32Array>,
  sourceUnit: GeckoMeasurementUnit
): Float32Array {
  const displayUnit = resolveValueDisplayUnit(sourceUnit)
  if (displayUnit === sourceUnit) {
    return values as Float32Array
  }

  const convertedValues = new Float32Array(values.length)
  for (let index = 0; index < values.length; index++) {
    const value = values[index]
    convertedValues[index] = Number.isFinite(value)
      ? GeckoMeasurement.CreateFromValue(value, sourceUnit).toNumber(
          displayUnit
        )
      : value
  }

  return convertedValues
}

export function createValueRange(
  values: Float32Array,
  unit: GeckoMeasurementUnit
): GeckoMeasurementRange | undefined {
  let min = Number.POSITIVE_INFINITY
  let max = Number.NEGATIVE_INFINITY

  for (const value of values) {
    if (!Number.isFinite(value)) {
      continue
    }

    if (value < min) {
      min = value
    }
    if (value > max) {
      max = value
    }
  }

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return undefined
  }

  return GeckoMeasurementRange.CreateFromValues(min, max, unit)
}

export function determineDefaultColorScaleThresholdNonCdf(
  selectedCdfView: GeckoView | undefined,
  measurementUnit?: GeckoMeasurementUnit,
  valueRange?: GeckoMeasurementRange
) {
  if (selectedCdfView === GeckoView.RemainingLife) {
    return GeckoColorScaleThreshold.CreateFromValues(
      0,
      50,
      150,
      GeckoMeasurementUnit.Years
    )
  }

  if (
    selectedCdfView === GeckoView.OutOfRoundness ||
    selectedCdfView === GeckoView.BfcRolloutMean ||
    selectedCdfView === GeckoView.BfcRolloutPeak ||
    selectedCdfView === GeckoView.RoundnessRolloutMean ||
    selectedCdfView === GeckoView.RoundnessRolloutPeak ||
    selectedCdfView === GeckoView.PlumnessRolloutMean ||
    selectedCdfView === GeckoView.PlumnessRolloutPeak
  ) {
    return GeckoColorScaleThreshold.CreateFromValues(
      -3.5,
      0,
      3.5,
      GeckoMeasurementUnit.Inches
    )
  }

  if (measurementUnit?.dataType === DataType.Categorical) {
    return GeckoColorScaleThreshold.CreateFromValues(
      measurementUnit.acceptableValueRange[0],
      (measurementUnit.acceptableValueRange[0] +
        measurementUnit.acceptableValueRange[1]) /
        2,
      measurementUnit.acceptableValueRange[1],
      measurementUnit
    )
  }

  if (!valueRange) {
    return undefined
  }

  return GeckoColorScaleThreshold.CreateFromMeasurements(
    valueRange.min,
    valueRange.min.plus(valueRange.max).scale(0.5),
    valueRange.max
  )
}
